import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useParams, useLocation, useNavigate } from 'react-router-dom';
import LiveBiddingAppBar from '../../../components/AuctionFeatureComponents/Bidding/LiveBiddingAppBar';
import TeamsTab from '../../../components/AuctionFeatureComponents/Bidding/tabs/TeamsTab';
import PlayersTab from '../../../components/AuctionFeatureComponents/Bidding/tabs/PlayersTab';
import SoldPlayersTab from '../../../components/AuctionFeatureComponents/Bidding/tabs/SoldPlayersTab';
import UnSoldPlayersTab from '../../../components/AuctionFeatureComponents/Bidding/tabs/UnSoldPlayersTab';
import axios from 'axios';
// import BidsList from '../../../components/AuctionFeatureComponents/Bidding/tabs/BidsList';
import AdminLiveBiddingTab from '../../../components/AuctionFeatureComponents/Bidding/AdminLiveBiddingTab';
import AuctionMainScreenOverlay from '../../../components/AuctionFeatureComponents/Overlays/MainScreen/AuctionMainScreenOverlay';
import UserLiveBiddingTab from '../../../components/AuctionFeatureComponents/Bidding/UserLiveBiddingTab';
import OBSOverlay from '../../../components/AuctionFeatureComponents/Overlays/obs/OBSOverlay';
import { checkIfAdmin } from '../../../Utils/AuthUtils';
import PassedPlayers from '../../../components/AuctionFeatureComponents/Bidding/tabs/PassedPlayers';
import { BACKEND_DOMAIN } from '../../../common/env';
import { Button, Box, Typography, Card } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import LiveAuctionUpdates from '../../../components/AuctionFeatureComponents/Bidding/LiveAuctionUpdates';
// Import the Navbar component from the home page
import Navbar from '../../../components/AuctionFeatureComponents/Home/Navbar';
import BidsList from '../../../components/AuctionFeatureComponents/Bidding/tabs/BidsList';

const LiveBiddingPage = () => {
    const [auctionName, setAuctionName] = useState("");
    const [auctionDate, setAuctionDate] = useState("");
    const [auction, setAuction] = useState(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const { auctionId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    // Check if current route is OBS overlay or main screen overlay
    const isOBSOverlay = location.pathname.includes('/obs-overlay');
    const isMainScreenOverlay = location.pathname.includes('/main-screen-overlay');

    // Helper function to extract YouTube video ID from URL
    const extractYouTubeId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    // Add navigation listener to redirect to auctions page when navigating back
    useEffect(() => {
        // Push a new state to the history stack
        window.history.pushState(null, null, window.location.href);
        
        // Add a listener for the popstate event (browser back/forward)
        const handlePopState = () => {
            // Redirect to auctions page
            navigate('/auctions');
        };
        
        window.addEventListener('popstate', handlePopState);
        
        // Cleanup listener on component unmount
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    useEffect(() => {
        const fetchAuctionName = async () => {
          try {
            const token = localStorage.getItem("token");
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
              headers,
            });
            setAuctionName(response.data.title || "Untitled Auction");
            setAuctionDate(response.data.date || "");
            setAuction(response.data);
            
            // Check if live stream is available
            if (response.data.liveAuctionLink) {
              const videoId = extractYouTubeId(response.data.liveAuctionLink);
              setIsVideoLoaded(!!videoId);
            }
          } catch (error) {
            console.error("Error fetching auction name:", error);
          }
        };
        fetchAuctionName();
      }, [auctionId]);
    
      const [isAdmin, setIsAdmin] = useState(false);

      // Check if the user is an admin (only if they have a token)
      useEffect(() => {
        const verifyAdminStatus = async () => {
          const token = localStorage.getItem("token");
          if (token) {
            try {
              const isAdminStatus = await checkIfAdmin(auctionId);
              setIsAdmin(isAdminStatus);
            } catch (error) {
              console.error("Error checking admin status:", error);
              setIsAdmin(false); // Default to non-admin if check fails
            }
          } else {
            setIsAdmin(false); // No token means not admin
          }
        };
        verifyAdminStatus();
      }, [auctionId]);

      // Redirect to teams tab if on user-live-bidding and no live stream is available
      useEffect(() => {
        const isUserLiveBiddingRoute = location.pathname.includes('/user-live-bidding');
        
        // If we're on the user-live-bidding route and there's no video, redirect to teams
        if (isUserLiveBiddingRoute && !isVideoLoaded && auction !== null) {
          navigate(`/live-auction/${auctionId}/teams`);
        }
      }, [location.pathname, isVideoLoaded, auction, auctionId, navigate]);

    return(
        <div>
            {/* Add the home page Navbar to user-live-bidding and admin live bidding pages */}
            {/* Show Navbar on all routes except OBS overlay and main screen overlay */}
            {!isOBSOverlay && !isMainScreenOverlay && <Navbar />}
            
            {/* Only show auction info if NOT on OBS overlay or main screen overlay routes */}
            {!isOBSOverlay && !isMainScreenOverlay && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    my: 2,
                    width: "100%",
                    maxWidth: "1200px",
                    margin: "20px auto 0",
                    flexDirection: "column",
                    padding: { xs: "0 10px", sm: "0 15px", md: "0 20px" },
                    // Add top margin to account for Navbar height
                    marginTop: { xs: "80px", sm: "80px", md: "80px" }
                }}>
                    <Box sx={{ 
                        textAlign: "center",
                        width: "100%"
                    }}>
                        <Typography variant="h4" sx={{ 
                            fontWeight: "bold", 
                            color: "#1f2937", 
                            mb: 0, // Remove bottom margin
                            display: "inline-block",
                            fontSize: { xs: "1.3rem", sm: "1.75rem", md: "2rem" },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: { xs: "90%", sm: "auto" }
                        }}>
                            {auctionName}
                        </Typography>
                        {auctionDate && (
                            <Typography variant="body1" sx={{ 
                                color: "#6b7280",
                                display: "block",
                                fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                                mt: 0 // Remove top margin
                            }}>
                                {new Date(auctionDate).toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                })}
                            </Typography>
                        )}
                    </Box>
                    {/* Horizontal line separator */}
                    <Box sx={{ 
                        width: "100%", 
                        height: "1px", 
                        backgroundColor: "#d1d5db", 
                        my: 2 
                    }} />
                </Box>
            )}
            
            {/* Live Auction Updates - Added below auction name/date and above LiveBiddingAppBar */}
            {/* {!isOBSOverlay && !isMainScreenOverlay && <LiveAuctionUpdates />} */}

            {/* Always show LiveBiddingAppBar except on OBS overlay */}
            {/* Add top margin to account for Navbar height */}
            {!isOBSOverlay && (
                <Box sx={{ marginTop: { xs: 0, sm: 0, md: 0 } }}>
                    <LiveBiddingAppBar />
                </Box>
            )}
            
            {/* Content container with border to create table-like appearance - hidden for OBS and main screen overlays */}
            {(!isOBSOverlay && !isMainScreenOverlay) ? (
                <div style={{ 
                    border: "1px solid #d1d5db", 
                    borderTop: "none", 
                    borderRadius: "0 0 6px 6px",
                    margin: "0 auto",
                    width: "100%",
                    maxWidth: "1200px",
                    minHeight: "calc(100vh - 200px)",
                    marginBottom: "20px",
                    padding: 0,
                    // Add top margin to account for Navbar height
                    marginTop: "0"
                }}>
                    <Routes>
                        {/* Provide both admin and user routes - users will be directed to appropriate route */}
                        <Route path="admin-live-bidding" element={<AdminLiveBiddingTab/>} />
                        <Route path="user-live-bidding" element={<UserLiveBiddingTab/>} />
                        <Route path="teams" element={<TeamsTab />} />
                        <Route path="players" element={<PlayersTab />} />
                        <Route path="sold-players" element={<SoldPlayersTab />} />
                        <Route path="unsold-players" element={<UnSoldPlayersTab />} />
                        <Route path="passed-players" element={<PassedPlayers />} />
                        <Route path="bids" element={<BidsList />} />
                    </Routes>
                </div>
            ) : (
                <Routes>
                    <Route path="main-screen-overlay" element={<AuctionMainScreenOverlay/>} />
                    <Route path="obs-overlay" element={<OBSOverlay/>} />
                </Routes>
            )}
        </div>
    )
}

export default LiveBiddingPage;